/**
 * Fantasy Console 0 - Map Utilities
 *
 * Core algorithms for map manipulation, tile operations,
 * and import/export functionality.
 */

import {
  MAP_WIDTH,
  MAP_HEIGHT,
  SPRITE_SIZE,
  SPRITE_SHEET_SIZE,
  SPRITES_PER_ROW,
} from '@fc0/types'
import { PICO8_RGB } from './spriteUtils'

/** Map dimensions */
export const MAP_TOTAL_TILES = MAP_WIDTH * MAP_HEIGHT // 8192

/**
 * Get tile at position in map
 */
export function getMapTile(mapData: Uint8Array, x: number, y: number): number {
  if (x < 0 || x >= MAP_WIDTH || y < 0 || y >= MAP_HEIGHT) {
    return 0
  }
  return mapData[y * MAP_WIDTH + x]
}

/**
 * Set tile at position in map (immutable)
 */
export function setMapTile(
  mapData: Uint8Array,
  x: number,
  y: number,
  tile: number,
): Uint8Array {
  if (x < 0 || x >= MAP_WIDTH || y < 0 || y >= MAP_HEIGHT) {
    return mapData
  }
  const newMap = new Uint8Array(mapData)
  newMap[y * MAP_WIDTH + x] = tile
  return newMap
}

/**
 * Set tile at position in map (mutating)
 */
export function setMapTileMut(
  mapData: Uint8Array,
  x: number,
  y: number,
  tile: number,
): void {
  if (x < 0 || x >= MAP_WIDTH || y < 0 || y >= MAP_HEIGHT) {
    return
  }
  mapData[y * MAP_WIDTH + x] = tile
}

/**
 * Clear entire map (set all tiles to 0)
 */
export function clearMap(): Uint8Array {
  return new Uint8Array(MAP_TOTAL_TILES)
}

/**
 * Fill entire map with a tile
 */
export function fillMapWithTile(tile: number): Uint8Array {
  const mapData = new Uint8Array(MAP_TOTAL_TILES)
  mapData.fill(tile)
  return mapData
}

/**
 * Flood fill algorithm for map tiles
 */
export function floodFillMap(
  mapData: Uint8Array,
  startX: number,
  startY: number,
  newTile: number,
): Uint8Array {
  if (startX < 0 || startX >= MAP_WIDTH || startY < 0 || startY >= MAP_HEIGHT) {
    return mapData
  }

  const targetTile = getMapTile(mapData, startX, startY)
  if (targetTile === newTile) {
    return mapData
  }

  const newMap = new Uint8Array(mapData)
  const stack: [number, number][] = [[startX, startY]]
  const visited = new Set<string>()

  while (stack.length > 0) {
    const [x, y] = stack.pop()!
    const key = `${x},${y}`

    if (visited.has(key)) continue
    if (x < 0 || x >= MAP_WIDTH || y < 0 || y >= MAP_HEIGHT) continue
    if (newMap[y * MAP_WIDTH + x] !== targetTile) continue

    visited.add(key)
    newMap[y * MAP_WIDTH + x] = newTile

    stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1])
  }

  return newMap
}

/**
 * Fill a rectangular region of the map with a tile
 */
export function fillRectMap(
  mapData: Uint8Array,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  tile: number,
  filled: boolean = true,
): Uint8Array {
  const newMap = new Uint8Array(mapData)

  const minX = Math.max(0, Math.min(x0, x1))
  const maxX = Math.min(MAP_WIDTH - 1, Math.max(x0, x1))
  const minY = Math.max(0, Math.min(y0, y1))
  const maxY = Math.min(MAP_HEIGHT - 1, Math.max(y0, y1))

  if (filled) {
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        newMap[y * MAP_WIDTH + x] = tile
      }
    }
  } else {
    // Draw outline only
    for (let x = minX; x <= maxX; x++) {
      newMap[minY * MAP_WIDTH + x] = tile
      newMap[maxY * MAP_WIDTH + x] = tile
    }
    for (let y = minY + 1; y < maxY; y++) {
      newMap[y * MAP_WIDTH + minX] = tile
      newMap[y * MAP_WIDTH + maxX] = tile
    }
  }

  return newMap
}

/**
 * Get a rectangular region from the map
 */
export function getMapRegion(
  mapData: Uint8Array,
  x: number,
  y: number,
  width: number,
  height: number,
): Uint8Array {
  const region = new Uint8Array(width * height)

  for (let dy = 0; dy < height; dy++) {
    for (let dx = 0; dx < width; dx++) {
      region[dy * width + dx] = getMapTile(mapData, x + dx, y + dy)
    }
  }

  return region
}

/**
 * Set a rectangular region in the map
 */
export function setMapRegion(
  mapData: Uint8Array,
  x: number,
  y: number,
  width: number,
  height: number,
  regionData: Uint8Array,
): Uint8Array {
  const newMap = new Uint8Array(mapData)

  for (let dy = 0; dy < height; dy++) {
    for (let dx = 0; dx < width; dx++) {
      const mapX = x + dx
      const mapY = y + dy
      if (mapX >= 0 && mapX < MAP_WIDTH && mapY >= 0 && mapY < MAP_HEIGHT) {
        newMap[mapY * MAP_WIDTH + mapX] = regionData[dy * width + dx]
      }
    }
  }

  return newMap
}

/**
 * Export map as CSV string
 */
export function exportMapAsCsv(mapData: Uint8Array): string {
  const rows: string[] = []

  for (let y = 0; y < MAP_HEIGHT; y++) {
    const row: number[] = []
    for (let x = 0; x < MAP_WIDTH; x++) {
      row.push(mapData[y * MAP_WIDTH + x])
    }
    rows.push(row.join(','))
  }

  return rows.join('\n')
}

/**
 * Import map from CSV string
 */
export function importMapFromCsv(csv: string): Uint8Array {
  const rows = csv.trim().split('\n')
  const mapData = new Uint8Array(MAP_TOTAL_TILES)

  for (let y = 0; y < MAP_HEIGHT; y++) {
    const row = rows[y] || ''
    const tiles = row.split(',').map((t) => {
      const parsed = parseInt(t.trim(), 10)
      return isNaN(parsed) ? 0 : Math.max(0, Math.min(255, parsed))
    })

    for (let x = 0; x < MAP_WIDTH; x++) {
      mapData[y * MAP_WIDTH + x] = tiles[x] || 0
    }
  }

  return mapData
}

/**
 * Download map as CSV file
 */
export function downloadMapAsCsv(
  mapData: Uint8Array,
  filename: string = 'map.csv',
): void {
  const csv = exportMapAsCsv(mapData)
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Import map from CSV file
 */
export function importMapFromFile(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const csv = e.target?.result as string
        const mapData = importMapFromCsv(csv)
        resolve(mapData)
      } catch (error) {
        reject(new Error('Failed to parse CSV file'))
      }
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsText(file)
  })
}

/**
 * Export map as PNG image
 */
export function exportMapAsPng(
  mapData: Uint8Array,
  spriteData: Uint8Array,
  filename: string = 'map.png',
): void {
  const canvas = document.createElement('canvas')
  const mapPixelWidth = MAP_WIDTH * SPRITE_SIZE
  const mapPixelHeight = MAP_HEIGHT * SPRITE_SIZE
  canvas.width = mapPixelWidth
  canvas.height = mapPixelHeight

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  // Create image data for the entire map
  const imageData = ctx.createImageData(mapPixelWidth, mapPixelHeight)

  for (let tileY = 0; tileY < MAP_HEIGHT; tileY++) {
    for (let tileX = 0; tileX < MAP_WIDTH; tileX++) {
      const tileIndex = tileY * MAP_WIDTH + tileX
      const spriteIndex = mapData[tileIndex]

      // Get sprite position in sprite sheet
      const spriteX = (spriteIndex % SPRITES_PER_ROW) * SPRITE_SIZE
      const spriteY = Math.floor(spriteIndex / SPRITES_PER_ROW) * SPRITE_SIZE

      // Copy sprite pixels to map image
      for (let py = 0; py < SPRITE_SIZE; py++) {
        for (let px = 0; px < SPRITE_SIZE; px++) {
          const srcIndex = (spriteY + py) * SPRITE_SHEET_SIZE + (spriteX + px)
          const colorIndex = spriteData[srcIndex]
          const [r, g, b] = PICO8_RGB[colorIndex]

          const destX = tileX * SPRITE_SIZE + px
          const destY = tileY * SPRITE_SIZE + py
          const destIndex = (destY * mapPixelWidth + destX) * 4

          imageData.data[destIndex] = r
          imageData.data[destIndex + 1] = g
          imageData.data[destIndex + 2] = b
          imageData.data[destIndex + 3] = 255
        }
      }
    }
  }

  ctx.putImageData(imageData, 0, 0)

  canvas.toBlob((blob) => {
    if (!blob) return

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, 'image/png')
}

/**
 * Render a single tile from the sprite sheet
 */
export function renderTileToImageData(
  spriteData: Uint8Array,
  tileIndex: number,
  imageData: ImageData,
  destX: number,
  destY: number,
  imageWidth: number,
): void {
  const spriteX = (tileIndex % SPRITES_PER_ROW) * SPRITE_SIZE
  const spriteY = Math.floor(tileIndex / SPRITES_PER_ROW) * SPRITE_SIZE

  for (let py = 0; py < SPRITE_SIZE; py++) {
    for (let px = 0; px < SPRITE_SIZE; px++) {
      const srcIndex = (spriteY + py) * SPRITE_SHEET_SIZE + (spriteX + px)
      const colorIndex = spriteData[srcIndex]
      const [r, g, b] = PICO8_RGB[colorIndex]

      const dstIndex = ((destY + py) * imageWidth + (destX + px)) * 4
      imageData.data[dstIndex] = r
      imageData.data[dstIndex + 1] = g
      imageData.data[dstIndex + 2] = b
      imageData.data[dstIndex + 3] = 255
    }
  }
}

/**
 * Get the average/dominant color of a sprite (for minimap)
 */
export function getSpriteDominantColor(
  spriteData: Uint8Array,
  spriteIndex: number,
): [number, number, number] {
  const spriteX = (spriteIndex % SPRITES_PER_ROW) * SPRITE_SIZE
  const spriteY = Math.floor(spriteIndex / SPRITES_PER_ROW) * SPRITE_SIZE

  // Count color occurrences
  const colorCounts = new Array(16).fill(0)

  for (let py = 0; py < SPRITE_SIZE; py++) {
    for (let px = 0; px < SPRITE_SIZE; px++) {
      const srcIndex = (spriteY + py) * SPRITE_SHEET_SIZE + (spriteX + px)
      const colorIndex = spriteData[srcIndex]
      colorCounts[colorIndex]++
    }
  }

  // Find most common non-black color (or black if all black)
  let maxCount = 0
  let dominantColor = 0

  for (let i = 0; i < 16; i++) {
    // Reduce black colorCount to prefer other colors unless black is a significant majority
    const count = colorCounts[i] - (i === 0 ? 25 : 0)

    if (count > maxCount) {
      maxCount = count
      dominantColor = i
    }
  }

  return PICO8_RGB[dominantColor]
}

/**
 * Convert screen coordinates to tile coordinates
 */
export function screenToTile(
  screenX: number,
  screenY: number,
  canvasRect: DOMRect,
  cameraX: number,
  cameraY: number,
  zoom: number,
): { x: number; y: number } {
  const canvasX = screenX - canvasRect.left
  const canvasY = screenY - canvasRect.top
  const tileSize = SPRITE_SIZE * zoom

  const tileX = Math.floor(canvasX / tileSize) + cameraX
  const tileY = Math.floor(canvasY / tileSize) + cameraY

  return { x: tileX, y: tileY }
}

/**
 * Check if tile coordinates are within map bounds
 */
export function isValidTilePosition(x: number, y: number): boolean {
  return x >= 0 && x < MAP_WIDTH && y >= 0 && y < MAP_HEIGHT
}

/**
 * Calculate visible tiles in viewport
 */
export function getVisibleTileRange(
  cameraX: number,
  cameraY: number,
  viewportWidthPx: number,
  viewportHeightPx: number,
  zoom: number,
): { startX: number; startY: number; endX: number; endY: number } {
  const tileSize = SPRITE_SIZE * zoom
  const tilesWide = Math.ceil(viewportWidthPx / tileSize) + 1
  const tilesHigh = Math.ceil(viewportHeightPx / tileSize) + 1

  return {
    startX: Math.max(0, Math.floor(cameraX)),
    startY: Math.max(0, Math.floor(cameraY)),
    endX: Math.min(MAP_WIDTH, Math.floor(cameraX) + tilesWide),
    endY: Math.min(MAP_HEIGHT, Math.floor(cameraY) + tilesHigh),
  }
}
